/**
 * Which line the pointer is on: the chip beside the pointer and the +1 width
 * (charts.md, "The line under the pointer").
 *
 * Pure so it can be tested without SciChart. The shell turns each candidate's
 * value at the snapped bar into a pixel; this picks among them.
 */

import type { SeriesKind } from "./series.js";

/** How close counts as "on" a line, in pixels (owner, 2026-10-07). */
export const PLOT_HIT_TOLERANCE = 6;

/**
 * Whether a series of this kind can be pointed at.
 *
 * Lines, mountains and markers (scatter, and signals, which draw as scatter).
 * A candle is the legend's top row, a band's value is its edge's, and a column
 * is a bar rather than a line. Markers were left out at first, and pointing at
 * a row of dots showed nothing (owner, 2026-10-07).
 */
export function isHittableKind(kind: SeriesKind): boolean {
  return kind === "line" || kind === "mountain" || kind === "scatter";
}

/**
 * The index of the bar at exactly `time`, or `-1`.
 *
 * Exact, not nearest: the crosshair has already snapped to a bar, and a series
 * that has no point there (a warm-up, another symbol's calendar) has nothing to
 * report at it.
 */
export function indexOfTime(at: (index: number) => number, length: number, time: number): number {
  let lo = 0;
  let hi = length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const t = at(mid);
    if (t === time) return mid;
    if (t < time) lo = mid + 1;
    else hi = mid - 1;
  }
  return -1;
}

/**
 * The candidate nearest `pointerY` within `tolerance`, or `undefined`.
 *
 * A tie goes to the earlier candidate, which is the one drawn first. A
 * non-finite y (a warm-up NaN) is never hit.
 */
export function nearestPlot<T extends { y: number }>(
  candidates: readonly T[],
  pointerY: number,
  tolerance = PLOT_HIT_TOLERANCE,
): T | undefined {
  let best: T | undefined;
  let bestDistance = Infinity;
  for (const c of candidates) {
    if (!Number.isFinite(c.y)) continue;
    const distance = Math.abs(c.y - pointerY);
    if (distance <= tolerance && distance < bestDistance) {
      best = c;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * The chip's name for a plot.
 *
 * A family drawing several rows heads them in the legend, and its rows carry
 * only output names (`Long Stop`). The chip has no heading above it, so it
 * prefixes the family: `Chandelier · Long Stop`. A one-row family's name
 * already carries the family label, parameters and all.
 */
export function chipName(name: string, family: string | undefined, familyRows: number): string {
  return family && familyRows > 1 ? `${family} · ${name}` : name;
}

/** Gap between the crosshair and the chip, in pixels. */
export const CHIP_GAP = 6;

/**
 * Where the chip goes: just **left of the crosshair's vertical line**, sitting
 * on its horizontal one; flipped to the **right** when there is no room before
 * the plot area's left edge (owner, 2026-10-07). Viewport pixels throughout.
 */
export function chipPlacement(
  hit: { x: number; y: number; left: number },
  chip: { width: number; height: number },
): { left: number; top: number; side: "left" | "right" } {
  const top = hit.y - chip.height - CHIP_GAP;
  const leftOf = hit.x - CHIP_GAP - chip.width;
  return leftOf >= hit.left
    ? { left: leftOf, top, side: "left" }
    : { left: hit.x + CHIP_GAP, top, side: "right" };
}
